import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { API, ENV } from '../env'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import router from '@/router'

export class HttpService {
  private readonly _http: AxiosInstance

  constructor(baseUrlApi = API[ENV()]()) {
    this._http = axios.create({
      baseURL: baseUrlApi,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      validateStatus: status => {
        if (status === 404) {
          console.error('404 Not Found')
        }
        return status >= 200 && status < 300
      }
    })

    // Interceptor de respuesta para error 403
    this._http.interceptors.response.use(
      response => response,
      async error => {
        if (!error.response) {
          return Promise.reject(error)
        }
        const { status, config } = error.response
        const isReportRequest =
          (config.url ?? '').includes('reports') >= 0 ||
          (config.data ?? '').includes('pg_reports_requests') >= 0
        // solo si la solicitud original fue un reporte o una solicitud de estado de algun reporte
        if (status === 403 && isReportRequest) {
          await this.refreshTokenAndRetryRequest(config)
          return this._http(config)
        }
        // Manejo de errores 401 y 403
        if (status === 401 || status === 403) {
          return this.unAuthorized()
        }

        // Manejo de errores 500 y 504 con reintentos
        const isCalcRequest = (config.url ?? '').includes('calc-controller')
        if ((status === 500 || status === 504) && !isCalcRequest) {
          if (!config.__retryCount) {
            config.__retryCount = 0
          }
          // Limitar a 3 reintentos
          if (config.__retryCount < 3) {
            config.__retryCount += 1
            await new Promise(resolve => setTimeout(resolve, 3000))
            return this._http(config)
          } else {
            console.error(
              'La solicitud falló después de varios intentos. Por favor, intenta de nuevo más tarde.'
            )
            return Promise.reject(error)
          }
        }
        // Manejo de otros errores
        return Promise.reject(error)
      }
    )
  }
  // desloguear usuario y redirigir a login
  unAuthorized() {
    const user = useUserStore()
    user.tokenInvalid = true
    void user.logout()
    void router.push({ name: 'Login' })
  }
  // Refrescar token y generar solicitud original
  private async refreshTokenAndRetryRequest(
    opts: AxiosRequestConfig<any>
  ): Promise<AxiosRequestConfig<any>> {
    try {
      const userStore = useUserStore()
      const payrollStore = usePayrollStore()
      // Actualizar headers de la solicitud original con el nuevo token
      if (opts && opts.headers) {
        opts.headers.Authorization = await userStore.getValidToken()
        opts.headers['x-country-id'] = payrollStore.payroll?.country.id
        opts.headers['x-country-code'] = payrollStore.payroll?.country.iso_code
        opts.headers['x-company-id'] = payrollStore.payroll?.company.id
        opts.headers['x-payroll-id'] = payrollStore.payroll?.id
        opts.headers['x-channel'] = 'APS-WEB'
        opts.headers['user-timezone'] =
          Intl.DateTimeFormat().resolvedOptions().timeZone
      }
      return opts
    } catch (error) {
      // Si el token no es valido, desloguear usuario
      return void this.unAuthorized() || {}
    }
  }

  async get<R>(
    pathUrl: string,
    opts?: (AxiosRequestConfig<any> & { withBearer?: boolean }) | undefined
  ) {
    return this._http.get<R>(pathUrl, await this.replaceHeader(opts))
  }

  async getWithHeaders<R>(
    pathUrl: string,
    opts?: (AxiosRequestConfig<any> & { withBearer?: boolean }) | undefined
  ) {
    return this._http.get<R>(pathUrl, await Promise.resolve(opts))
  }

  async post<T, R>(
    pathUrl: string,
    data: T,
    opts?: AxiosRequestConfig<any> | undefined
  ) {
    return this._http.post<R>(pathUrl, data, await this.replaceHeader(opts))
  }

  async update<T, R>(
    pathUrl: string,
    data: T,
    opts?: AxiosRequestConfig<any> | undefined
  ) {
    return this._http.post<R>(pathUrl, data, await this.replaceHeader(opts))
  }

  async put<T, R>(
    pathUrl: string,
    data: T,
    opts?: AxiosRequestConfig<any> | undefined
  ) {
    return this._http.put<R>(pathUrl, data, await this.replaceHeader(opts))
  }

  async delete<R>(pathUrl: string, opts?: AxiosRequestConfig<any> | undefined) {
    return this._http.delete<R>(pathUrl, await this.replaceHeader(opts))
  }

  // eslint-disable-next-line @typescript-eslint/prefer-readonly
  private replaceHeader(
    opts: (AxiosRequestConfig<any> & { withBearer?: boolean }) | undefined
  ): Promise<AxiosRequestConfig<any>> {
    const userStore = useUserStore()
    const payrollStore = usePayrollStore()
    const withBearer = opts?.withBearer ?? true

    const rawToken = userStore.user?.token?.token
    const Authorization = rawToken
      ? withBearer
        ? `Bearer ${rawToken}`
        : rawToken
      : undefined
    const newHeaders = {
      ...opts?.headers,
      Authorization,
      'x-country-id': payrollStore.payroll?.country.id,
      'x-country-code': payrollStore.payroll?.country.iso_code,
      'x-company-id': payrollStore.payroll?.company.id,
      'x-payroll-id': payrollStore.payroll?.id,
      'x-channel': 'APS-WEB',
      'user-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
    }
    if (opts) {
      opts.headers = newHeaders
    } else {
      opts = {
        headers: newHeaders
      }
    }
    return Promise.resolve(opts)
  }
}
